import { useState } from "react";
import { IMAGES } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import { ArrowRight } from "lucide-react";

const categories = ["All", "Field Reports", "Beneficiary Spotlights", "Impact Updates", "Media Coverage"];

const stories = [
  {
    id: 1,
    image: IMAGES.healthcare,
    date: "November 22, 2025",
    category: "Field Reports",
    title: "Blood Donation Camp at SGVS Hospital, Khunti",
    excerpt: "MBT Trust organized a major blood donation camp in partnership with Jharkhand Health Department at SGVS Hospital premises, Khunti.",
    content: "MBT Welfare & Development Trust organized a major blood donation camp at SGVS Hospital, Khunti on November 22, 2025, in partnership with the District Health Department and Jharkhand Navoday Morcha. The camp witnessed enthusiastic participation from community members who donated blood to support local healthcare facilities. This initiative is part of MBT's broader healthcare programme aimed at strengthening medical infrastructure in tribal areas of Jharkhand.",
  },
  {
    id: 2,
    image: IMAGES.youth,
    date: "September 28, 2025",
    category: "Impact Updates",
    title: "Youth Leadership Summit 2025: Building Tomorrow's Changemakers",
    excerpt: "Over 200 young leaders gathered for our annual summit, participating in workshops on civic engagement, social entrepreneurship, and community development.",
    content: "The Youth Leadership Summit 2025 brought together over 200 young leaders from across Jharkhand for a three-day intensive programme. Participants engaged in workshops on civic engagement, social entrepreneurship, digital literacy, and community development. The summit featured panel discussions with established social leaders, hackathons for solving local problems, and networking sessions that connected young changemakers with mentors and resources.",
  },
  {
    id: 3,
    image: IMAGES.children,
    date: "August 12, 2025",
    category: "Beneficiary Spotlights",
    title: "Community Nutrition Drive Benefits 300+ Children",
    excerpt: "Our nutrition awareness programme distributed healthy meals and conducted health check-ups for children across rural Jharkhand, addressing malnutrition in tribal communities.",
    content: "Our community nutrition drive reached over 300 children across 8 villages in Khunti district. The programme combined nutritious meal distribution with comprehensive health check-ups, growth monitoring, and nutrition education for parents. Community health workers were trained to continue monitoring children's nutritional status and provide ongoing support to families.",
  },
  {
    id: 4,
    image: IMAGES.womenEmpowerment,
    date: "July 5, 2025",
    category: "Beneficiary Spotlights",
    title: "Women's SHG Network Crosses 1,800 Households",
    excerpt: "Our women's self-help group network has grown to serve over 1,800 households, with members building micro-enterprises and achieving financial independence.",
    content: "The MBT Women's Self-Help Group network has crossed the milestone of serving 1,800 households across multiple districts. These groups provide financial literacy training, micro-loan facilities, vocational skill development, and leadership training. Many SHG members have successfully started small businesses, from tailoring units to food processing enterprises, creating sustainable livelihoods for their families.",
  },
  {
    id: 5,
    image: IMAGES.agriculture,
    date: "June 18, 2025",
    category: "Field Reports",
    title: "FPO Training Programme Empowers 500 Farmers with Modern Techniques",
    excerpt: "Our Farmer Producer Organisation conducted a comprehensive training programme, teaching 500 farmers modern agricultural techniques and connecting them with direct market access.",
    content: "The FPO training programme equipped 500 small and marginal farmers with modern agricultural techniques including organic farming practices, water conservation methods, and post-harvest management. Farmers were also connected with direct market linkages, bypassing middlemen and significantly improving their income. The programme included hands-on demonstrations, field visits, and ongoing mentoring support.",
  },
  {
    id: 6,
    image: IMAGES.events,
    date: "May 30, 2025",
    category: "Media Coverage",
    title: "MBT Trust Featured in National Media for Grassroots Impact",
    excerpt: "MBT Welfare & Development Trust received national media coverage for its integrated approach to community development in Jharkhand's tribal regions.",
    content: "MBT Welfare & Development Trust was featured in major national media outlets for its unique integrated approach to community development. The coverage highlighted how MBT's model of combining agriculture, healthcare, education, and women empowerment creates compounding impact in tribal communities. The story showcased testimonials from beneficiaries and documented the trust's journey from a small local initiative to a multi-district organisation.",
  },
];

export default function StoriesPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedStory, setSelectedStory] = useState(null);

  const filtered = activeCategory === "All" ? stories : stories.filter((s) => s.category === activeCategory);

  if (selectedStory) {
    return (
      <main data-testid="story-detail-page">
        <section className="relative min-h-[40vh] flex items-end overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${selectedStory.image})` }} />
          <div className="absolute inset-0 bg-[#1B4332]/60" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 pb-12 pt-28">
            <p className="text-[#D4A843] text-sm font-outfit font-semibold mb-3">{selectedStory.category} | {selectedStory.date}</p>
            <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-white font-bold">{selectedStory.title}</h1>
          </div>
        </section>
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSelectedStory(null)}
              data-testid="back-to-stories"
              className="inline-flex items-center gap-2 text-[#2D6A4F] font-outfit font-semibold mb-8 hover:text-[#1B4332] transition-colors"
            >
              &larr; Back to Stories
            </button>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#333] font-outfit leading-relaxed text-lg">{selectedStory.content}</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="stories-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.heroHome})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/65" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
          <ScrollReveal>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4">
              Stories from the Ground
            </h1>
            <p className="text-white/80 text-base sm:text-lg font-outfit max-w-xl mx-auto">
              Real stories of transformation, resilience, and community-led change from across Jharkhand.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Filter + Stories */}
      <section className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex flex-wrap gap-2 mb-10 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-outfit font-medium transition-all ${
                    activeCategory === cat
                      ? "bg-[#1B4332] text-white"
                      : "bg-white text-[#1B4332] border border-[#2D6A4F]/20 hover:bg-[#1B4332]/5"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((story, i) => (
              <ScrollReveal key={story.id} delay={i * 0.1}>
                <button
                  onClick={() => setSelectedStory(story)}
                  data-testid={`story-card-${story.id}`}
                  className="block text-left bg-white rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(27,67,50,0.06)] hover:shadow-[0_8px_30px_rgba(27,67,50,0.12)] hover:-translate-y-1 transition-all w-full"
                >
                  <img src={story.image} alt={story.title} className="w-full h-48 object-cover" loading="lazy" />
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#D4A843] text-xs font-outfit font-semibold">{story.date}</span>
                      <span className="text-[#666] text-xs font-outfit">| {story.category}</span>
                    </div>
                    <h3 className="font-playfair text-lg text-[#1B4332] font-semibold mb-2 leading-snug">{story.title}</h3>
                    <p className="text-[#666] text-sm font-outfit leading-relaxed line-clamp-2">{story.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-[#D4A843] text-sm font-semibold font-outfit mt-4">
                      Read More <ArrowRight size={14} />
                    </span>
                  </div>
                </button>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
